export interface MIPostProcessOptions {
  bloom?: MIBloomOptions | boolean;
  ssao?: MISSAOOptions | boolean;
  depthOfField?: MIDepthOfFieldOptions | boolean;
  hdr?: MIHDROptions | boolean;
  fxaa?: boolean;
  taa?: boolean;
  customStages?: MIPostProcessStage[];
}

export interface MIBloomOptions {
  enabled: boolean;
  intensity?: number;
  threshold?: number;
  radius?: number;
  softKnee?: number;
}

export interface MISSAOOptions {
  enabled: boolean;
  intensity?: number;
  radius?: number;
  bias?: number;
  blurStepSize?: number;
  ambientOcclusionOnly?: boolean;
}

export interface MIDepthOfFieldOptions {
  enabled: boolean;
  focalDistance?: number;
  focalLength?: number;
  fStop?: number;
  focusTarget?: [number, number, number];
  delta?: number;
  near?: number;
  far?: number;
}

export interface MIHDROptions {
  enabled: boolean;
  toneMappingOperator?: 'reinhard' | 'filmic' | 'aces' | 'uncharted2' | 'none';
  exposure?: number;
  gamma?: number;
  whitePoint?: number;
  saturation?: number;
}

export interface MIPostProcessStage {
  name: string;
  fragmentShader: string;
  uniforms?: Record<string, { type: 'float' | 'vec2' | 'vec3' | 'vec4' | 'sampler2D' | 'bool' | 'int'; value: any }>;
  inputTextures?: string[];
  enabled?: boolean;
  order?: number;
}

export interface MIWeatherEffect {
  type: 'rain' | 'snow' | 'fog' | 'clouds' | 'dust' | 'custom';
  intensity?: number;
  rainSpeed?: number;
  rainSize?: number;
  rainAngle?: number;
  snowSize?: number;
  snowSpeed?: number;
  snowTurbulence?: number;
  fogDensity?: number;
  fogColor?: string;
  fogHeight?: number;
  cloudCoverage?: number;
  cloudAltitude?: number;
  cloudSpeed?: number;
  cloudColor?: string;
  dustColor?: string;
  dustSize?: number;
  particleCount?: number;
  particleImage?: string;
  particleSize?: [number, number];
  particleLifetime?: [number, number];
  emitterPosition?: [number, number, number];
  emitterRadius?: number;
  gravity?: [number, number, number];
  wind?: [number, number, number];
  startColor?: string;
  endColor?: string;
}

export interface MIWeatherHandle {
  id: string;
  setIntensity(intensity: number): void;
  getIntensity(): number;
  setEnabled(enabled: boolean): void;
  remove(): void;
}

export interface MIWaterSurfaceOptions {
  extent?: any;
  altitude?: number;
  color?: string;
  opacity?: number;
  reflectivity?: number;
  waveFrequency?: number;
  waveAmplitude?: number;
  waveSpeed?: number;
  specularIntensity?: number;
  environmentMapReflection?: boolean;
  underwater?: {
    enabled: boolean;
    visibility: number;
    color: string;
    caustics: boolean;
  };
}

export interface MIWaterHandle {
  id: string;
  setWaveAmplitude(amplitude: number): void;
  setAltitude(altitude: number): void;
  setColor(color: string): void;
  remove(): void;
}

const DEFAULT_BLOOM: MIBloomOptions = {
  enabled: false,
  intensity: 0.15,
  threshold: 0.9,
  radius: 0.4,
  softKnee: 0.5,
};

const DEFAULT_SSAO: MISSAOOptions = {
  enabled: false,
  intensity: 0.5,
  radius: 0.08,
  bias: 0.02,
  blurStepSize: 2.0,
  ambientOcclusionOnly: false,
};

const DEFAULT_DOF: MIDepthOfFieldOptions = {
  enabled: false,
  focalLength: 50,
  fStop: 5.6,
};

const DEFAULT_HDR: MIHDROptions = {
  enabled: false,
  toneMappingOperator: 'aces',
  exposure: 1.0,
  gamma: 2.2,
  whitePoint: 5.0,
  saturation: 1.0,
};

let effectIdCounter = 0;
function generateEffectId(prefix: string): string {
  return `${prefix}_${++effectIdCounter}_${Date.now()}`;
}

function resolveBloomOptions(input: MIBloomOptions | boolean | undefined): MIBloomOptions {
  if (input === undefined) return { ...DEFAULT_BLOOM };
  if (typeof input === 'boolean') return { ...DEFAULT_BLOOM, enabled: input };
  return { ...DEFAULT_BLOOM, ...input };
}

function resolveSSAOOptions(input: MISSAOOptions | boolean | undefined): MISSAOOptions {
  if (input === undefined) return { ...DEFAULT_SSAO };
  if (typeof input === 'boolean') return { ...DEFAULT_SSAO, enabled: input };
  return { ...DEFAULT_SSAO, ...input };
}

function resolveDOFOptions(input: MIDepthOfFieldOptions | boolean | undefined): MIDepthOfFieldOptions {
  if (input === undefined) return { ...DEFAULT_DOF };
  if (typeof input === 'boolean') return { ...DEFAULT_DOF, enabled: input };
  return { ...DEFAULT_DOF, ...input };
}

function resolveHDROptions(input: MIHDROptions | boolean | undefined): MIHDROptions {
  if (input === undefined) return { ...DEFAULT_HDR };
  if (typeof input === 'boolean') return { ...DEFAULT_HDR, enabled: input };
  return { ...DEFAULT_HDR, ...input };
}

export class PostProcessManager {
  private _options: MIPostProcessOptions;
  private _bloom: MIBloomOptions;
  private _ssao: MISSAOOptions;
  private _depthOfField: MIDepthOfFieldOptions;
  private _hdr: MIHDROptions;
  private _fxaa: boolean;
  private _taa: boolean;
  private _customStages: Map<string, MIPostProcessStage>;
  private _weatherEffects: Map<string, { effect: MIWeatherEffect; enabled: boolean; intensity: number }>;
  private _waterSurfaces: Map<string, MIWaterSurfaceOptions>;

  constructor(options?: MIPostProcessOptions) {
    this._options = options ? { ...options } : {};
    this._bloom = resolveBloomOptions(options?.bloom);
    this._ssao = resolveSSAOOptions(options?.ssao);
    this._depthOfField = resolveDOFOptions(options?.depthOfField);
    this._hdr = resolveHDROptions(options?.hdr);
    this._fxaa = options?.fxaa ?? false;
    this._taa = options?.taa ?? false;
    this._customStages = new Map();
    this._weatherEffects = new Map();
    this._waterSurfaces = new Map();

    if (options?.customStages) {
      for (const stage of options.customStages) {
        this._customStages.set(stage.name, { ...stage });
      }
    }
  }

  setOptions(options: MIPostProcessOptions): void {
    this._options = { ...options };

    if (options.bloom !== undefined) {
      this._bloom = resolveBloomOptions(options.bloom);
    }
    if (options.ssao !== undefined) {
      this._ssao = resolveSSAOOptions(options.ssao);
    }
    if (options.depthOfField !== undefined) {
      this._depthOfField = resolveDOFOptions(options.depthOfField);
    }
    if (options.hdr !== undefined) {
      this._hdr = resolveHDROptions(options.hdr);
    }
    if (options.fxaa !== undefined) {
      this._fxaa = options.fxaa;
    }
    if (options.taa !== undefined) {
      this._taa = options.taa;
    }
    if (options.customStages !== undefined) {
      this._customStages.clear();
      for (const stage of options.customStages) {
        this._customStages.set(stage.name, { ...stage });
      }
    }
  }

  getOptions(): MIPostProcessOptions {
    return {
      bloom: { ...this._bloom },
      ssao: { ...this._ssao },
      depthOfField: { ...this._depthOfField },
      hdr: { ...this._hdr },
      fxaa: this._fxaa,
      taa: this._taa,
      customStages: Array.from(this._customStages.values()).map((s) => ({ ...s })),
    };
  }

  setDepthOfField(options: MIDepthOfFieldOptions | boolean): void {
    this._depthOfField = resolveDOFOptions(options);
    this._options.depthOfField = options;
  }

  setHDR(options: MIHDROptions | boolean): void {
    this._hdr = resolveHDROptions(options);
    this._options.hdr = options;
  }

  addCustomStage(stage: MIPostProcessStage): void {
    this._customStages.set(stage.name, { ...stage });
  }

  removeCustomStage(name: string): void {
    this._customStages.delete(name);
  }

  addWeatherEffect(effect: MIWeatherEffect): MIWeatherHandle {
    const id = generateEffectId('weather');
    const intensity = effect.intensity ?? 1.0;

    this._weatherEffects.set(id, {
      effect: { ...effect },
      enabled: true,
      intensity,
    });

    const self = this;

    return {
      id,
      setIntensity(newIntensity: number): void {
        const entry = self._weatherEffects.get(id);
        if (entry) {
          entry.intensity = Math.max(0, Math.min(1, newIntensity));
          entry.effect.intensity = entry.intensity;
        }
      },
      getIntensity(): number {
        const entry = self._weatherEffects.get(id);
        return entry ? entry.intensity : 0;
      },
      setEnabled(enabled: boolean): void {
        const entry = self._weatherEffects.get(id);
        if (entry) {
          entry.enabled = enabled;
        }
      },
      remove(): void {
        self._weatherEffects.delete(id);
      },
    };
  }

  addWaterSurface(options: MIWaterSurfaceOptions): MIWaterHandle {
    const id = generateEffectId('water');
    const stored: MIWaterSurfaceOptions = { ...options };

    this._waterSurfaces.set(id, stored);

    const self = this;

    return {
      id,
      setWaveAmplitude(amplitude: number): void {
        const surface = self._waterSurfaces.get(id);
        if (surface) {
          surface.waveAmplitude = amplitude;
        }
      },
      setAltitude(altitude: number): void {
        const surface = self._waterSurfaces.get(id);
        if (surface) {
          surface.altitude = altitude;
        }
      },
      setColor(color: string): void {
        const surface = self._waterSurfaces.get(id);
        if (surface) {
          surface.color = color;
        }
      },
      remove(): void {
        self._waterSurfaces.delete(id);
      },
    };
  }

  // ─── Cesium scene wiring ─────────────────────────────────────────────────
  private _attachedViewer: any = null;
  private _attachedStages: any[] = [];

  /**
   * Attach this manager to a Cesium viewer. Configures the scene's built-in
   * post-process stages (bloom, SSAO via ambientOcclusion, FXAA) and adds
   * weather stages where Cesium ships built-ins (rain, snow). Subsequent
   * setBloom / setSSAO / setFXAA calls re-apply.
   */
  attachToScene(viewer: any): void {
    if (!viewer || !viewer.scene) return;
    this.detachFromScene();
    this._attachedViewer = viewer;
    this._applyAll();
  }

  detachFromScene(): void {
    const v = this._attachedViewer;
    if (v?.scene?.postProcessStages) {
      for (const s of this._attachedStages) {
        try { v.scene.postProcessStages.remove(s); } catch {}
      }
    }
    this._attachedStages = [];
    this._attachedViewer = null;
  }

  private _applyAll(): void {
    const v = this._attachedViewer;
    const Cesium = (globalThis as any).Cesium;
    if (!v || !v.scene || !Cesium) return;
    const stages = v.scene.postProcessStages;
    try {
      if (stages.bloom) {
        stages.bloom.enabled = !!this._bloom.enabled;
        if (stages.bloom.uniforms) {
          if (this._bloom.intensity !== undefined) stages.bloom.uniforms.glowOnly = false;
          if (this._bloom.threshold !== undefined) stages.bloom.uniforms.brightness = this._bloom.threshold;
          if (this._bloom.radius !== undefined) stages.bloom.uniforms.contrast = this._bloom.radius;
        }
      }
      if (stages.ambientOcclusion) {
        stages.ambientOcclusion.enabled = !!this._ssao.enabled;
        if (stages.ambientOcclusion.uniforms) {
          if (this._ssao.intensity !== undefined) stages.ambientOcclusion.uniforms.intensity = this._ssao.intensity;
          if (this._ssao.bias !== undefined) stages.ambientOcclusion.uniforms.bias = this._ssao.bias;
        }
      }
      if (stages.fxaa) stages.fxaa.enabled = !!this._fxaa;
    } catch {}
    // HDR exposure → scene
    if (v.scene && this._hdr.enabled) {
      try {
        if (this._hdr.exposure !== undefined) v.scene.brightness = this._hdr.exposure;
      } catch {}
    }
    // Weather effects
    for (const [, entry] of this._weatherEffects) {
      if (!entry.enabled) continue;
      try {
        const lib = Cesium.PostProcessStageLibrary;
        let stage: any = null;
        if (entry.effect.type === 'rain' && lib?.createRainStage) stage = lib.createRainStage();
        else if (entry.effect.type === 'snow' && lib?.createSnowStage) stage = lib.createSnowStage();
        if (stage) {
          stages.add(stage);
          this._attachedStages.push(stage);
        }
        if (entry.effect.type === 'fog') {
          v.scene.fog.enabled = true;
          if (entry.effect.fogDensity !== undefined) v.scene.fog.density = entry.effect.fogDensity;
        }
      } catch {}
    }
  }

  setBloom(options: MIBloomOptions | boolean): void {
    this._bloom = resolveBloomOptions(options);
    this._options.bloom = options;
    this._applyAll();
  }

  setSSAO(options: MISSAOOptions | boolean): void {
    this._ssao = resolveSSAOOptions(options);
    this._options.ssao = options;
    this._applyAll();
  }

  setFXAA(enabled: boolean): void {
    this._fxaa = enabled;
    this._options.fxaa = enabled;
    this._applyAll();
  }

  destroy(): void {
    this.detachFromScene();
    this._customStages.clear();
    this._weatherEffects.clear();
    this._waterSurfaces.clear();
    this._bloom = { ...DEFAULT_BLOOM };
    this._ssao = { ...DEFAULT_SSAO };
    this._depthOfField = { ...DEFAULT_DOF };
    this._hdr = { ...DEFAULT_HDR };
    this._fxaa = false;
    this._taa = false;
    this._options = {};
  }
}
